<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useNotification } from 'src/composables/useNotification';
import { useConfirm } from 'src/composables/useConfirm';
import { useTicketService } from 'src/services/ticket-service';
import { getFetchRecipe, getFetchAction, getFetchAssignment } from 'src/api/queries/order-portal-bff';
import type { NestedAssignmentResponse2, FetchAssignmentResponse, FetchTicketResponse } from 'src/api/models';
import { calculateTotalHours } from 'src/utils/assignment-utils';
import OperationsTable from '../ticket/assignment/OperationsTable.vue';
import AddOperationDialog from '../ticket/AddOperationDialog.vue';
import TicketEditAssignmentForm from './TicketEditAssignmentForm.vue';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

interface FlattenedItem {
  id: string;
  type: 'assignment' | 'sub-action';
  data: any;
  parentId?: string;
  level: number;
  isRecipe?: boolean;
}

interface HoursByType {
  [key: string]: number;
  total: number;
}

const props = defineProps<{
  ticketId?: string;
  ticket?: FetchTicketResponse;
}>();

const emit = defineEmits<{ 'refresh-ticket': [] }>();

const { showSuccess } = useNotification();
const { confirm } = useConfirm();
const { deleteAssignment } = useTicketService();

const assignments = computed(() => props.ticket?.assignments ?? []);
const loading = ref(false);
const refreshing = ref(false);
const showAddForm = ref(false);
const operationDialogVisible = ref(false);
const selectedAssignment = ref<NestedAssignmentResponse2 | null>(null);
const selectedAssignmentName = ref<string>('');
const dialogMode = ref<'add' | 'edit'>('edit');
const assignmentDetailData = ref<Map<string, FetchAssignmentResponse>>(new Map());
const recipeData = ref<Map<string, any>>(new Map());
const actionData = ref<Map<string, any>>(new Map());
const dataLoading = ref<Map<string, boolean>>(new Map());
const flattenedItems = ref<FlattenedItem[]>([]);

function getDetailData(actionId?: string): any | null {
  if (!actionId) return null;
  return recipeData.value.get(actionId) ?? actionData.value.get(actionId) ?? null;
}

const hoursByType = computed<HoursByType>(() => {
  const result: HoursByType = { total: 0 };
  assignments.value.forEach((assignment) => {
    if (!assignment.id) return;
    const fullAssignment = assignmentDetailData.value.get(assignment.id);
    const labours = fullAssignment?.action_labours ?? [];
    labours.forEach((labor) => {
      const hours = labor.total_hours || 0;
      const type = String(labor.type || '');
      if (type) result[type] = (result[type] || 0) + hours;
      result.total += hours;
    });
  });
  return result;
});

async function fetchDetailData() {
  const toFetch = assignments.value.filter((a) => a.id && a.action);
  if (!toFetch.length) return;

  loading.value = true;
  try {
    await Promise.all(
      toFetch.map(async (assignment) => {
        const assignmentId = assignment.id!;
        const actionId = assignment.action!;
        if (dataLoading.value.get(assignmentId)) return;

        dataLoading.value.set(assignmentId, true);
        try {
          const assignmentResponse = await getFetchAssignment(assignmentId);
          if (assignmentResponse) assignmentDetailData.value.set(assignmentId, assignmentResponse);

          if (assignment.action_type === 'recipe') {
            const recipeResponse = await getFetchRecipe(actionId);
            if (recipeResponse) recipeData.value.set(actionId, recipeResponse);
          } else {
            const actionResponse = await getFetchAction(actionId);
            if (actionResponse) actionData.value.set(actionId, actionResponse);
          }
        } catch (error) {
          console.error(`Error fetching details for assignment ${assignmentId}:`, error);
        } finally {
          dataLoading.value.set(assignmentId, false);
        }
      }),
    );
  } finally {
    loading.value = false;
  }
  buildFlattenedStructure();
}

function buildFlattenedStructure() {
  const result: FlattenedItem[] = [];
  assignments.value.forEach((assignment) => {
    const actionId = assignment.action;
    const isRecipeItem = assignment.action_type === 'recipe';
    result.push({
      id: assignment.id || `assignment-${Math.random()}`,
      type: 'assignment',
      data: assignment,
      level: 0,
      isRecipe: isRecipeItem,
    });
    if (isRecipeItem && actionId) {
      const recipe = recipeData.value.get(actionId);
      if (recipe?.actions) {
        recipe.actions.forEach((subAction: any, index: number) => {
          result.push({
            id: subAction.id || `sub-action-${actionId}-${index}`,
            type: 'sub-action',
            data: subAction,
            parentId: assignment.id,
            level: 1,
          });
        });
      }
    }
  });
  flattenedItems.value = result;
}

function isActionLoading(actionId?: string): boolean {
  return actionId ? !!dataLoading.value.get(actionId) : false;
}

function getActionName(item: FlattenedItem): string {
  if (item.type === 'assignment') {
    const actionId = item.data.action as string | undefined;
    if (actionId) {
      const detail = getDetailData(actionId);
      if (detail?.name) return detail.name;
    }
    return actionId ? `Assignment #${actionId.substring(0, 6)}` : 'Unknown Assignment';
  }
  return item.data.name || 'Unnamed Sub-action';
}

function getLaborInfo(item: FlattenedItem): { assignees: Array<any>; hours: number } {
  const fullAssignment = item.data.id ? assignmentDetailData.value.get(item.data.id) : undefined;
  const labours = fullAssignment?.action_labours ?? [];
  const assignees = new Map();
  labours.forEach((labor: any) => {
    if (labor.assignee?.id) assignees.set(labor.assignee.id, labor.assignee);
  });
  return { assignees: Array.from(assignees.values()), hours: calculateTotalHours(labours) };
}

function getItemLaborHours(item: any): number {
  return item.type !== 'assignment' ? 0 : getLaborInfo(item).hours;
}

function getItemLaborAssignees(item: any): Array<{ id: string; name: string }> {
  return item.type !== 'assignment' ? [] : getLaborInfo(item).assignees;
}

function getFinancialData(item: any, type: 'charged' | 'actual' | 'quote'): string {
  if (item.type === 'assignment') {
    const fullAssignment = item.data.id ? assignmentDetailData.value.get(item.data.id) : undefined;
    const labours = fullAssignment?.action_labours ?? [];
    if (type === 'charged') return '0,00';
    const rate = props.ticket?.assignee?.price_per_hour ?? 0;
    const labourType = type === 'actual' ? 'variable' : 'fixed';
    const hours = labours
      .filter((l: any) => l.type === labourType)
      .reduce((sum: number, l: any) => sum + (l.total_hours || 0), 0);
    return formatNumber(hours * rate);
  }
  // if (item.type === 'sub-action' && item.data.price) return formatNumber(item.data.price);
  return '0,00';
}

function getFinancialTotal(type: 'charged' | 'actual' | 'quote'): string {
  const total = flattenedItems.value
    .filter((item) => item.type === 'assignment')
    .reduce((sum, item) => {
      const value = getFinancialData(item, type);
      return sum + (parseFloat(value.replace('.', '').replace(',', '.')) || 0);
    }, 0);
  return formatNumber(total);
}

function formatNumber(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) || 0 : value;
  return num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function handleRefresh() {
  refreshing.value = true;
  assignmentDetailData.value.clear();
  recipeData.value.clear();
  actionData.value.clear();
  dataLoading.value.clear();
  flattenedItems.value = [];
  emit('refresh-ticket');
  refreshing.value = false;
  showSuccess({ message: 'Refreshing assignment data...' });
}

function handleAddOperation() {
  showAddForm.value = !showAddForm.value;
}

function handleAssignmentAdded() {
  emit('refresh-ticket');
}

function handleEditAssignment(assignment: NestedAssignmentResponse2) {
  if (!assignment.id) return;
  selectedAssignment.value = assignment;
  const actionId = assignment.action as string | undefined;
  selectedAssignmentName.value = (actionId && getDetailData(actionId)?.name) || '';
  dialogMode.value = 'edit';
  operationDialogVisible.value = true;
}

async function handleDeleteAssignment(assignment: NestedAssignmentResponse2, event: Event) {
  if (!assignment.id || !props.ticketId) return;
  const { onAccept } = confirm(event);
  onAccept(async () => {
    if (!assignment.id) return;
    await deleteAssignment(assignment.id);
    buildFlattenedStructure();
    emit('refresh-ticket');
  });
}

function updateAssignmentStatus(_assignmentId: string, _newStatus: string) {
  buildFlattenedStructure();
}

watch(assignments, () => fetchDetailData(), { immediate: true });
</script>

<template>
  <div class="flex min-h-0">
    <div class="flex-1 p-8 min-w-0">
      <TicketEditSectionHeader title="Assignments" :count="assignments.length" class="mb-4">
        <template #actions>
          <div class="flex items-center gap-1.5">
            <button class="action-btn" :class="{ 'opacity-50': refreshing }" @click="handleRefresh">
              <i class="pi pi-refresh" :class="{ 'pi-spin': refreshing }" style="font-size: 13px" />
            </button>
          </div>
        </template>
      </TicketEditSectionHeader>

      <OperationsTable
      :items="flattenedItems"
      :loading="loading"
      :hours-by-type="hoursByType"
      :get-action-name="(getActionName as any)"
      :is-action-loading="isActionLoading"
      :get-labor-hours="getItemLaborHours"
      :get-labor-assignees="getItemLaborAssignees"
      :get-financial-data="getFinancialData"
      :get-financial-total="getFinancialTotal"
      @edit-assignment="handleEditAssignment"
      @delete-assignment="handleDeleteAssignment"
    />

    <AddOperationDialog
      v-model:visible="operationDialogVisible"
      :ticket-id="ticketId"
      :existing-assignment="selectedAssignment"
      :action-name="selectedAssignmentName"
      mode="edit"
      :ticket-assignee="(ticket?.assignee as any)"
      @operation-updated="() => emit('refresh-ticket')"
      @update:status="updateAssignmentStatus"
    />
    </div>

    <TicketEditAssignmentForm
      :ticket-id="ticketId!"
      :ticket-assignee="(ticket?.assignee as any)"
      @assignment-added="handleAssignmentAdded"
    />
  </div>
</template>
